import { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
    mdiChevronRight,
    mdiMinus,
    mdiPlus,
    mdiSchool,
    mdiDotsVertical,
    mdiTransitConnectionVariant,
} from '@mdi/js';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

import {
    CourseStudentTokenDocument,
    CourseManagerTokenDocument,
    CourseTrainerTokenDocument,
    DeleteCourseDocument,
    ICourseFragment,
    ICourseListItemFragment,
    CourseDocument,
    IPortfolioItemProgressFragment,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { Icon } from 'common/components/Icon';
import { IconButton } from 'common/components/IconButton';
import {
    ListItem,
    ListItemActionText,
    ListItemMedia,
    ListItemSecondaryAction,
    ListItemText,
    ListItemFooter,
    ListItemButton,
} from 'common/components/ListItem';
import { DropdownMenu } from 'common/components/DropdownMenu';
import { Chip, ChipGroup } from 'common/components/Chip';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { ExtraCategoryLabels } from 'organisation/components/ExtraCategoryLabels';
import { CourseCopyAction } from 'training/actions/CourseCopyAction';
import { Tooltip } from 'common/components/Tooltip';
import { useFrontendPermissions } from 'user/hooks';
import { PortfolioItemProgressScoreChip } from 'user/components/PortfolioItemProgressScoreChip';
import { TFrontendRole } from 'user/types';

interface IProps extends React.ComponentProps<typeof ListItem> {
    addButton?: boolean;
    added?: boolean;
    disabled?: boolean;
    portfolioItem?: IPortfolioItemProgressFragment | null;
    selected?: boolean;
    course: ICourseListItemFragment | ICourseFragment;
    showActions?: boolean;
    footer?: React.ReactNode;
    loading?: boolean;
    hideCategoryChips?: boolean;
    hideChips?: boolean;
    /** User id to open course trainer view for */
    forUserId?: string;
    /** External (LTI) participant id to open course trainer view for */
    pluvoUserId?: number | null;
    linkToTrainerView?: boolean;
    onClick?(course: ICourseListItemFragment | ICourseFragment): void;
}

export const CourseListItem = ({
    addButton,
    added,
    disabled,
    portfolioItem,
    selected,
    course,
    onClick,
    showActions,
    footer,
    loading,
    hideCategoryChips,
    hideChips,
    forUserId,
    pluvoUserId,
    linkToTrainerView,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const { showApolloError } = useApolloError();
    const [displaySnackbar] = useSnackbar();
    const { canUpdate: canUpdateCourse, userRoles } =
        useFrontendPermissions('course');

    const isManager = !!userRoles?.includes(TFrontendRole.Manager);

    // Trainer view for an external (LTI) participant is manager-only.
    const isTrainerViewRestricted =
        !!linkToTrainerView && pluvoUserId != null && !isManager;

    const isDisabled = disabled || isTrainerViewRestricted;

    const [showDeleteAlert, setShowDeleteAlert] = useState(false);

    const [getCourseManagerToken, { loading: loadingManagerToken }] =
        useLazyQuery(CourseManagerTokenDocument);

    const [getCourseStudentToken, { loading: loadingStudentToken }] =
        useLazyQuery(CourseStudentTokenDocument);

    const [getTrainerToken, { loading: loadingTrainerToken }] = useLazyQuery(
        CourseTrainerTokenDocument
    );

    const [deleteCourse, { loading: loadingDelete }] =
        useMutation(DeleteCourseDocument);

    const loadingAction =
        loading || loadingManagerToken || loadingStudentToken || loadingDelete;

    const {
        id,
        title,
        image,
        thumbnail,
        publishedVersionNumber,
        extraCategoryValues,
        trainingCount,
        offerEventCount,
    } = course;

    const listItemImage = thumbnail?.url || image?.url || '';

    let mediaIconPath = mdiSchool;

    if (addButton) {
        mediaIconPath = selected || added ? mdiMinus : mdiPlus;
    }
    const hasIcon = (isDisabled && !listItemImage) || !isDisabled;
    const versionChipLabel = translate(
        publishedVersionNumber ? 'version' : 'draft',
        { version: publishedVersionNumber }
    );

    const openCourseEdit = async () => {
        // Get url without params
        const returnUrl = window.location.href;
        let url: string | undefined;

        try {
            const response = await getCourseManagerToken({
                variables: { id, returnUrl },
            });

            url = response.data?.courseManagerToken?.url;
        } catch (error) {
            showApolloError(error);

            return;
        }

        if (!url) return;

        window.location.href = url;
    };

    const openCourseView = async () => {
        const returnUrl = window.location.href;
        let url: string | undefined;

        try {
            const response = await getCourseStudentToken({
                variables: { id, returnUrl },
            });

            url = response.data?.courseStudentToken?.url;
        } catch (error) {
            showApolloError(error);

            return;
        }

        if (!url) return;

        window.location.href = url;
    };

    const openCourseTrainerView = async () => {
        if (isTrainerViewRestricted) return;

        const returnUrl = window.location.href;

        let url: string | undefined;

        try {
            const response = await getTrainerToken({
                variables: {
                    id,
                    returnUrl,
                    forUser: forUserId,
                    forPluvoUserId: pluvoUserId,
                },
            });

            url = response.data?.courseTrainerToken?.url;
        } catch (error) {
            showApolloError(error);

            return;
        }

        if (!url) return;

        window.location.href = url;
    };

    const handleDeleteCourse = async () => {
        try {
            await deleteCourse({
                variables: {
                    id,
                },
                update: (cache) => {
                    // Remove course list from cache so it will be refetched
                    cache.evict({ fieldName: 'courses' });

                    // Use writeQuery to update course query cache so it's properly removed
                    cache.writeQuery({
                        query: CourseDocument,
                        data: { course: null },
                        variables: { id },
                    });

                    cache.gc();
                },
            });
        } catch (error) {
            showApolloError(error);
            setShowDeleteAlert(false);

            return;
        }

        displaySnackbar(translate('courseActionSuccess.delete'), {
            variant: 'success',
        });

        setShowDeleteAlert(false);
    };

    const itemActionMenu = showActions && (
        <DropdownMenu
            anchor={
                <IconButton
                    aria-label={translate('actions')}
                    color="inherit"
                    iconPath={mdiDotsVertical}
                    iconSize="2.4rem"
                />
            }
            placement="bottom-end"
        >
            {/* Only show "view" item if course is published */}
            {!!publishedVersionNumber && (
                <MenuItem disabled={loadingAction} onClick={openCourseView}>
                    {translate('view')}
                </MenuItem>
            )}
            <MenuItem disabled={loadingAction} onClick={openCourseEdit}>
                {translate('edit')}
            </MenuItem>
            <CourseCopyAction id={id}>
                {({ copyCourse, loading: loadingCopy }) => (
                    <MenuItem
                        disabled={loadingAction || loadingCopy}
                        onClick={copyCourse}
                    >
                        {translate('copy')}
                    </MenuItem>
                )}
            </CourseCopyAction>
            <MenuItem
                disabled={loadingAction}
                onClick={() => {
                    setShowDeleteAlert(true);
                }}
            >
                {translate('delete')}
            </MenuItem>
        </DropdownMenu>
    );

    const showActionText =
        !itemActionMenu &&
        !addButton &&
        !isDisabled &&
        ((loading && portfolioItem) || (!loading && !portfolioItem));

    let actionText = (
        <>
            <ListItemActionText>{translate('open')}</ListItemActionText>
            <ListItemSecondaryAction>
                <Icon path={mdiChevronRight} size="3rem" />
            </ListItemSecondaryAction>
        </>
    );

    const isLoading = loading || loadingTrainerToken;

    if (isLoading) {
        // Show progress icon when loading
        actionText = (
            <ListItemSecondaryAction>
                <CircularProgress color="primary" size={30} />
            </ListItemSecondaryAction>
        );
    }

    const baseContent = (
        <>
            <ListItemMedia color="primary" image={listItemImage} size="small">
                {hasIcon && (
                    <Icon path={mediaIconPath} size="2rem" sizeSm="3rem" />
                )}
            </ListItemMedia>

            <ListItemText primary={title}>
                {!hideChips && canUpdateCourse && (
                    <ChipGroup>
                        <Chip
                            bgColor={
                                publishedVersionNumber ? undefined : 'error'
                            }
                            label={versionChipLabel}
                        />
                        {!!trainingCount && (
                            <Tooltip
                                title={translate('trainingCount', {
                                    count: trainingCount,
                                })}
                            >
                                <Box>
                                    <Chip
                                        icon={
                                            <Icon
                                                path={
                                                    mdiTransitConnectionVariant
                                                }
                                                size="1.4rem"
                                            />
                                        }
                                        label={trainingCount}
                                        size="small"
                                    />
                                </Box>
                            </Tooltip>
                        )}
                        {!!offerEventCount && (
                            <Tooltip
                                title={translate('offerEventCount', {
                                    count: offerEventCount,
                                })}
                            >
                                <Box>
                                    <Chip
                                        icon={
                                            <Icon
                                                path={mdiSchool}
                                                size="1.4rem"
                                            />
                                        }
                                        label={offerEventCount}
                                        size="small"
                                    />
                                </Box>
                            </Tooltip>
                        )}
                        {!hideCategoryChips && (
                            <ExtraCategoryLabels
                                extraCategoryValues={extraCategoryValues}
                            />
                        )}
                    </ChipGroup>
                )}
            </ListItemText>

            {showActionText && actionText}
        </>
    );

    return (
        <>
            <ListItem
                button={!isDisabled}
                disableHover={isDisabled}
                key={id}
                px={0}
                selected={selected}
                tabIndex={-1}
                {...other}
            >
                {isDisabled ? (
                    baseContent
                ) : (
                    <ListItemButton
                        onClick={() => {
                            onClick?.(course);

                            // If linkToTrainerView is true, open course trainer view
                            linkToTrainerView && openCourseTrainerView();
                        }}
                    >
                        {baseContent}
                    </ListItemButton>
                )}

                {itemActionMenu && (
                    <ListItemSecondaryAction hideXs>
                        {itemActionMenu}
                    </ListItemSecondaryAction>
                )}

                {!isLoading && !!portfolioItem && (
                    <PortfolioItemProgressScoreChip
                        portfolioItem={portfolioItem}
                    />
                )}

                {footer && <ListItemFooter>{footer}</ListItemFooter>}
            </ListItem>

            <ConfirmDialog
                confirmText={translate('delete')}
                loading={loadingDelete}
                open={showDeleteAlert}
                title={translate('deleteCourseMessage.title')}
                onCancel={() => {
                    setShowDeleteAlert(false);
                }}
                onClose={() => setShowDeleteAlert(false)}
                onConfirm={handleDeleteCourse}
            >
                {translate('deleteCourseMessage.text')}
            </ConfirmDialog>
        </>
    );
};
